// AI 主决策：优先级 濒死救援 > 自救 > 攻击最低血敌人 > 控制 > 装备/收益 > 结束回合
// 所有候选动作经引擎试算（一 ply 前瞻：含对方响应窗口）后按团队局势分排序
import type { GameState, PlayerState, Team, StatusInstance } from "@/types/game";
import type { DeckCard } from "@/types/cards";
import type { EngineAction } from "@/engine/GameEngine";
import { findSubstituteConfig } from "@/engine/GameEngine";
import {
  enumerateLegalActions,
  requiredDiscardCount,
  buildDiscardAction,
  supplyHealHp,
} from "@/engine/LegalActions";
import {
  evaluateState,
  simulateAfterAction,
  tryApply,
  cardValue,
} from "./heuristics";
import { decideResponderAction } from "./responder";

const BAD_STATUSES = new Set([
  "寒冰",
  "沉默",
  "禁攻",
  "无魔御",
  "无间炼狱",
  "致命弱点",
  "剧毒",
]);

function playerOf(state: GameState, id: string): PlayerState {
  return state.players.find((p) => p.id === id)!;
}

function cardOf(state: GameState, playerId: string, uid: number) {
  return playerOf(state, playerId).hand.find((c) => c.uid === uid);
}

// 替换动作填位：选价值最低的2张手牌（满足花色限制）填入 substituteCardUids
function fillSubstituteUids(state: GameState, action: EngineAction): EngineAction {
  if (action.type === "play-card" && action.substituteCardUids && action.substituteCardUids[0] === -1) {
    const player = playerOf(state, action.playerId);
    const sub = findSubstituteConfig(player, "active");
    const needBlack = sub?.config.suitFilter === "黑";
    const pool = needBlack
      ? player.hand.filter((c) => c.suit === "黑")
      : [...player.hand];
    const picks = pool
      .sort((a, b) => cardValue(a, player) - cardValue(b, player))
      .slice(0, 2);
    return { ...action, substituteCardUids: [picks[0].uid, picks[1].uid] };
  }
  if (action.type === "respond" && action.substituteCardUids && action.substituteCardUids[0] === -1) {
    const player = playerOf(state, action.playerId);
    const sub = findSubstituteConfig(player, "respond");
    const needBlack = sub?.config.suitFilter === "黑";
    const pool = needBlack
      ? player.hand.filter((c) => c.suit === "黑")
      : [...player.hand];
    const picks = pool
      .sort((a, b) => cardValue(a, player) - cardValue(b, player))
      .slice(0, 2);
    return { ...action, substituteCardUids: [picks[0].uid, picks[1].uid] };
  }
  return action;
}

// 动作的启发式优先级加成（在局势分增量之外，固化计划中的优先级语义）
function priorityBonus(
  state: GameState,
  action: EngineAction,
  after: GameState
): number {
  if (action.type !== "play-card") return 0;
  const actor = playerOf(state, action.playerId);
  // 替换模式：cardUid=-1，构造虚拟牌用于启发式判断
  const card = action.substituteAs
    ? { name: action.substituteAs, kind: action.substituteAs.includes("攻击") ? "攻击牌" : "效果牌" } as DeckCard
    : cardOf(state, action.playerId, action.cardUid);
  if (!card) return 0;
  let bonus = 0;

  const tid = action.targetId;
  const beforeTarget = tid ? playerOf(state, tid) : undefined;
  const afterTarget = tid ? playerOf(after, tid) : undefined;

  // 1) 濒死救援：目标从濒死脱险（任何 hp>0 即可脱险）
  if (
    beforeTarget?.isDying &&
    afterTarget &&
    afterTarget.isAlive &&
    !afterTarget.isDying
  ) {
    bonus += 600;
    // 节约策略：优先用恢复量最小的药水救回（恢复5→10→15→20），
    // 把大生命药水等大瓶留给后续；×2 确保压过救回后 hp 高低带来的局势分差
    if (card.kind === "补给牌") {
      bonus -= supplyHealHp(state, card) * 2;
    }
  }

  // 2) 紧急自救/治疗：目标残血且获得有效治疗
  if (
    card.kind === "补给牌" ||
    action.skillName === "救赎" ||
    action.skillName === "天使降临"
  ) {
    if (beforeTarget && afterTarget && afterTarget.hp - beforeTarget.hp >= 10) {
      const ratio = beforeTarget.hp / beforeTarget.maxHp;
      if (ratio < 0.45 && !beforeTarget.isDying) bonus += 120;
    }
  }

  // 3) 控制：敌方新获得负面状态
  if (afterTarget && afterTarget.team !== actor.team) {
    const beforeNames = new Set(beforeTarget!.statuses.map((s) => s.name));
    const gained = afterTarget.statuses.filter(
      (s) => !beforeNames.has(s.name) && BAD_STATUSES.has(s.name)
    );
    bonus += gained.length * 40;
  }
  // 多目标控制/伤害技能：检查所有敌人
  if (action.skillName) {
    for (const bp of state.players) {
      if (bp.team === actor.team || !bp.isAlive) continue;
      const ap = after.players.find((p) => p.id === bp.id)!;
      const beforeNames = new Set(bp.statuses.map((s: StatusInstance) => s.name));
      const gained = ap.statuses.filter(
        (s) => !beforeNames.has(s.name) && BAD_STATUSES.has(s.name)
      );
      if (gained.length > 0 && bp.id !== tid) bonus += gained.length * 40;
    }
  }

  // 4) 装备：空槽上装备的稳定收益
  if (card.kind === "装备牌") {
    bonus += 6;
  }

  // 5) 免费收益
  if (card.name === "贪婪") bonus += 14; // 立即摸 2
  if (card.name === "嗜血") {
    // 手里还有 ≥1 张可打攻击/法术时才值得自损
    const hasMore = actor.hand.some(
      (c) => c.uid !== card.uid && (c.kind === "攻击牌" || c.kind === "法术牌")
    );
    if (hasMore) bonus += 24;
  }
  if (card.name === "拾遗" && action.takeFromDiscardUid !== undefined) {
    const got = after.players
      .find((p) => p.id === action.playerId)!
      .hand.find((c) => c.uid === action.takeFromDiscardUid);
    if (got) bonus += cardValue(got) * 1.5;
  }

  return bonus;
}

// 在候选动作中选出对 actorTeam 最优者（返回动作与试算后状态）
function chooseBest(
  state: GameState,
  actions: EngineAction[],
  team: Team
): { action: EngineAction; after: GameState; score: number } | null {
  let best: { action: EngineAction; after: GameState; score: number } | null = null;
  for (const rawAction of actions) {
    const action = fillSubstituteUids(state, rawAction);
    const after = simulateAfterAction(state, action);
    if (!after) continue; // 引擎拒绝的非法候选
    const delta = evaluateState(after, team) - evaluateState(state, team);
    const score = delta + priorityBonus(state, action, after);
    if (!best || score > best.score) best = { action, after, score };
  }
  return best;
}

// 弃牌：弃保留价值最低的 N 张
export function chooseDiscardUids(state: GameState, playerId: string): number[] {
  const player = playerOf(state, playerId);
  const n = requiredDiscardCount(player);
  return [...player.hand]
    .sort((a, b) => cardValue(a, player) - cardValue(b, player))
    .slice(0, n)
    .map((c) => c.uid);
}

// 非执行阶段玩家的急救动作（濒死队友）：返回最优者，无则 null
export function decideRescueAction(state: GameState): EngineAction | null {
  let best: { action: EngineAction; score: number } | null = null;
  for (const p of state.players) {
    if (!p.isAlive) continue;
    const actions = enumerateLegalActions(state, p.id);
    if (actions.length === 0) continue;
    const choice = chooseBest(state, actions, p.team);
    if (choice && (!best || choice.score > best.score)) {
      best = { action: choice.action, score: choice.score };
    }
  }
  // 急救动作必须是正收益且确实在救濒死者
  if (best && best.score > 200) return best.action;
  return null;
}

// 拾遗窗口决策：见 responder.ts 的 decidePickupAction（避免与 heuristics 循环依赖）
export { decidePickupAction } from "./responder";

// AI 决策主入口（按阶段分派）
export function decideAction(
  state: GameState,
  playerId: string
): EngineAction {
  const player = playerOf(state, playerId);

  if (state.phase === "draw") {
    return { type: "draw" };
  }
  if (state.phase === "response") {
    return decideResponderAction(state, playerId);
  }
  if (state.phase === "discard") {
    const overflow = requiredDiscardCount(player);
    if (overflow <= 0) return { type: "end-turn" };
    return buildDiscardAction(chooseDiscardUids(state, playerId));
  }
  if (state.phase === "play") {
    const candidates = enumerateLegalActions(state, playerId);
    const best = chooseBest(state, candidates, player.team);
    if (best && best.score > 0.5) return best.action;
    return { type: "end-turn" };
  }
  throw new Error(`AI 无法在阶段 ${state.phase} 决策`);
}

// 供驱动层做合法性兜底校验
export function isActionLegal(state: GameState, action: EngineAction): boolean {
  return tryApply(state, action) !== null;
}
